import React, { FunctionComponent, ReactNode } from "react";
import { useSelector } from "react-redux";
import { Route, Switch } from "react-router-dom";
import { selectUserRole } from "../../features/auth/authSlice";
import { UserRole } from "../../features/users/usersSlice";
import { CampaignReports } from "./Campaigns";
import { CasesReports } from "./Case";
import { CompanyReports } from "./Company";
import { EducationalResourcesReport } from "./EducationalResources";
import { KnowledgeBaseReports } from "./KnowledgeBase";
import { CoursesReport } from "./Learning/Courses";
import { LessonsReport } from "./Learning/Lessons";
import { TopicsReport } from "./Learning/Topics";
import { UsersReports } from "./Users";
import { Prefixes } from "../prefixes";
import { getTranslations } from "../../features/translations/translationsUtils";

interface PerCategoryProps {
  companyId: string;
}

enum ReportToolTips {
  day = "REPORT_TOOLTIPS_1",
  month = "REPORT_TOOLTIPS_2",
}

export enum ReportTypes {
  Users = "users",
  Companies = "companies",
  EducationalContent = "educationalContent",
  Lessons = "lessons",
  Topics = "topics",
  Courses = "courses",
  KnowledgeBase = "knowledge-bases",
  Campaigns = "campaign",
  Cases = "reportedCases",
}

export const ReportRoles: {
  [reportType: string]: UserRole[];
} = {
  [ReportTypes.Lessons]: [UserRole.SuperAdmin, UserRole.CompanyAdmin, UserRole.Supervisor],
  [ReportTypes.Topics]: [UserRole.SuperAdmin, UserRole.CompanyAdmin, UserRole.Supervisor],
  [ReportTypes.Courses]: [UserRole.SuperAdmin, UserRole.CompanyAdmin, UserRole.Supervisor],
  [ReportTypes.Users]: [UserRole.SuperAdmin, UserRole.CompanyAdmin, UserRole.Supervisor],
  [ReportTypes.Companies]: [UserRole.SuperAdmin],
  [ReportTypes.Campaigns]: [UserRole.SuperAdmin, UserRole.CompanyAdmin, UserRole.Supervisor],
  [ReportTypes.KnowledgeBase]: [UserRole.SuperAdmin, UserRole.CompanyAdmin, UserRole.Supervisor],
  [ReportTypes.EducationalContent]: [UserRole.SuperAdmin],
  [ReportTypes.Cases]: [UserRole.SuperAdmin, UserRole.CompanyAdmin],
};

interface IReportSectionTitles {
  [_id: string]: string;
}

export const BASE_REPORT_URL = "reports/";

export const sectionTooltips: Record<ReportTypes, ReportToolTips> = {
  [ReportTypes.Lessons]: ReportToolTips.day,
  [ReportTypes.Topics]: ReportToolTips.day,
  [ReportTypes.Courses]: ReportToolTips.day,
  [ReportTypes.Users]: ReportToolTips.month,
  [ReportTypes.Companies]: ReportToolTips.month,
  [ReportTypes.Campaigns]: ReportToolTips.day,
  [ReportTypes.KnowledgeBase]: ReportToolTips.month,
  [ReportTypes.EducationalContent]: ReportToolTips.day,
  [ReportTypes.Cases]: ReportToolTips.day,
};

export const menuTitles: () => Record<ReportTypes, string> = () => ({
  [ReportTypes.Lessons]: getTranslations('ADMIN_MAIN_MENU_LESSONS'),
  [ReportTypes.Topics]: getTranslations('ADMIN_MAIN_MENU_TOPICS'),
  [ReportTypes.Courses]: getTranslations('ADMIN_MAIN_MENU_COURSES'),
  [ReportTypes.Users]: getTranslations('ADMIN_MAIN_MENU_USERS'),
  [ReportTypes.Companies]: getTranslations('ADMIN_MAIN_MENU_COMPANIES'),
  [ReportTypes.Campaigns]: getTranslations('ADMIN_MAIN_MENU_CAMPAIGNS'),
  [ReportTypes.KnowledgeBase]: getTranslations('ADMIN_MAIN_MENU_KNOWLEDGE_BASE'),
  [ReportTypes.EducationalContent]: getTranslations('ADMIN_MAIN_MENU_EDUCATIONAL_CONTENT'),
  [ReportTypes.Cases]: getTranslations('ADMIN_MAIN_MENU_REPORTED_CASES'),
});

export const sectionTitles: () => IReportSectionTitles = () => ({
  [ReportTypes.Lessons]: getTranslations('REPORT_TITLE_LESSONS'),
  [ReportTypes.Topics]: getTranslations('REPORT_TITLE_TOPICS'),
  [ReportTypes.Courses]: getTranslations('REPORT_TITLE_COURSES'),
  [ReportTypes.Users]: getTranslations('REPORT_TITLE_USERS'),
  [ReportTypes.Companies]: getTranslations('REPORT_TITLE_COMPANIES'),
  [ReportTypes.Campaigns]: getTranslations('REPORT_TITLE_CAMPAIGNS'),
  [ReportTypes.KnowledgeBase]: getTranslations('REPORT_TITLE_KNOWLEDGE_BASE'),
  [ReportTypes.EducationalContent]: getTranslations('REPORT_TITLE_EDUCATIONAL_CONTENT'),
  [ReportTypes.Cases]: getTranslations('REPORT_TITLE_REPORTED_CASES'),
});

export const ReportsRoutes: FunctionComponent<PerCategoryProps> = ({
  companyId,
}) => {
  const userRole = useSelector(selectUserRole);
  const indexes: { [_id: string]: ReactNode } = {
    [ReportTypes.Users]: <UsersReports companyId={companyId} />,
    [ReportTypes.Lessons]: <LessonsReport companyId={companyId} />,
    [ReportTypes.Topics]: <TopicsReport companyId={companyId} />,
    [ReportTypes.Courses]: <CoursesReport companyId={companyId} />,
    [ReportTypes.Companies]: <CompanyReports />,
    [ReportTypes.Campaigns]: <CampaignReports companyId={companyId} />,
    [ReportTypes.KnowledgeBase]: <KnowledgeBaseReports companyId={companyId} />,
    [ReportTypes.EducationalContent]: <EducationalResourcesReport />,
    [ReportTypes.Cases]: <CasesReports companyId={companyId} />,
  };
  return (
    <Switch>
      {Object.values(ReportTypes)
        .filter(
          (reportType) => userRole && ReportRoles[reportType].includes(userRole)
        )
        .map((reportType) => {
          return (
            <Route
              key={reportType}
              path={`${Prefixes.reports}/${reportType}`}
              render={() => indexes[reportType]}
            />
          );
        })}
    </Switch>
  );
};
